import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useMemo } from 'react';
import { validateProps } from '../../../../utils/propValidator';
import { ModalWrapper } from '../feedback';
import { SectionTitle } from '../layout';

const propRules = {
  open: { type: 'boolean', required: true },
  onClose: { type: 'function', required: true },
  media: { type: ['array', 'string'], required: true },
  title: { type: 'string' },
};

/**
 * ImageViewer
 *
 * Read-only modal for previewing one or more images.
 *
 * @component
 *
 * @param {Object} props
 *
 * @param {boolean} props.open
 * Controls whether the viewer modal is open.
 *
 * @param {Function} props.onClose
 * Callback invoked when the modal is closed.
 *
 * @param {string | string[]} props.media
 * Image source or list of image sources.
 * Each entry must be a valid image URL.
 *
 * @param {string} [props.title]
 * Title displayed at the top of the viewer modal.
 *
 * @returns {JSX.Element}
 */
export default function ImageViewer(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'ImageViewer');
  const { open, onClose, media, title } = props;

  const normalizedMedia = useMemo(() => {
    if (typeof media === 'string') return [media];
    if (Array.isArray(media)) return media;
    return [];
  }, [media]);

  const isValidMedia =
    normalizedMedia.length > 0 && normalizedMedia.every((src) => typeof src === 'string');

  const multiple = normalizedMedia.length > 1;

  const isInvalid = !isValidMedia;

  const errorMessage = 'Invalid media source';

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      maxWidth={multiple ? (normalizedMedia.length > 2 ? 'lg' : 'sm') : 'sm'}
    >
      <SectionTitle title={title || 'Image Viewer'} />
      {isInvalid ? (
        <Alert severity='error'>{errorMessage}</Alert>
      ) : multiple ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: `repeat(${Math.min(media.length, 3)}, 1fr)`,
            },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {normalizedMedia.map((src, i) => (
            <Box
              key={i}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                '& > img': { width: '100%', height: 'auto', display: 'block', borderRadius: 1 },
              }}
            >
              <img src={src} alt='' />
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            maxHeight: '70vh',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            '& > img': {
              objectFit: 'contain',
              width: '100%',
              height: 'auto',
            },
          }}
        >
          <img src={normalizedMedia[0]} alt={title || 'Image preview'} />
        </Box>
      )}
    </ModalWrapper>
  );
}
